import React from 'react';
import { Box } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';

export interface IconTileProps {
  /** The icon to render inside the tile (typically a 20px MUI icon). */
  children: React.ReactNode;
  /**
   * Optional fill color. When provided, the tile uses this exact
   * CSS color as its background. When omitted, the theme's neutral
   * warm-stone surface (`palette.surfaceMuted`) is used — matches spec §3.4.
   */
  color?: string;
  /**
   * When set (and a `color` is provided), renders the "tinted wash"
   * treatment (Robinhood Direction 2): a low-alpha wash of `color`
   * (`alpha(color, 0.14)`) plus a thin accent ring
   * (`1px solid ${alpha(color, 0.5)}`) instead of a solid fill.
   * Without a `color`, this is a no-op and the default neutral fill is used.
   * Defaults to `false` — existing callers render exactly as before.
   */
  tint?: boolean;
}

/**
 * 40×40 rounded-square tile that frames an icon. Accepts an
 * explicit CSS color via `color`; otherwise falls back to a neutral
 * warm stone fill from the theme.
 *
 * With `tint`, a `color` is rendered as a low-alpha wash + accent ring
 * rather than a solid fill.
 */
export const IconTile: React.FC<IconTileProps> = ({ children, color, tint }) => {
  const theme = useTheme();
  // The tint treatment requires an accent color to wash; without one we
  // can't compute a tint, so fall back to the default solid behavior.
  const useTint = Boolean(tint && color);
  const background = useTint
    ? alpha(color as string, 0.14)
    : color ?? theme.palette.surfaceMuted;
  const border = useTint ? `1px solid ${alpha(color as string, 0.5)}` : undefined;
  return (
    <Box
      sx={{
        width: 40,
        height: 40,
        borderRadius: 2,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        // Inline style ensures the background color is present on the
        // DOM node so `element.style.backgroundColor` (and jsdom's
        // computed style) resolves to it reliably.
        backgroundColor: background,
        ...(border ? { border } : {}),
      }}
      // Also set background (and the ring, for the tint variant) as an inline
      // style so `element.style.backgroundColor` resolves reliably in jsdom.
      style={{ backgroundColor: background, ...(border ? { border } : {}) }}
    >
      {children}
    </Box>
  );
};

export default IconTile;
